import pulse from '@finn-no/pulse-sdk';
import type { Agent } from '../agent';
import { DEFAULT_SUB_VERTICAL } from '../subVertical';

const MEGLERPROMO_ID = 'effect-meglerpromo';

let currentSubVertical = DEFAULT_SUB_VERTICAL;

/** Set once at client bootstrap; tracking fires only in the browser, one podlet instance per page. */
export const setSubVertical = (subVertical: string): void => {
    currentSubVertical = subVertical;
};

const toVertical = () => ({ name: 'realestate', subVertical: currentSubVertical }) as const;

/** RE_ev136 — impression of the whole element. Fires once when it enters the viewport. */
export function trackViewMeglerpromo(): void {
    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'View meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Content',
        },
        vertical: toVertical(),
    });
}

/** RE_ev137 — impression of a single agent card. `position` is 0-based. */
export function trackViewAgent(agent: Agent, position: number): void {
    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'View agent in meglerpromo',
        object: {
            id: agent.phone,
            type: 'UIElement',
            elementType: 'Link',
            position: position.toString(),
            name: agent.orgId.toString(),
        },
        target: {
            id: `https://www.finn.no/meglerprofiler/${agent.phone}`,
            type: 'Page',
            name: agent.phone,
        },
        vertical: toVertical(),
    });
}

/** RE_ev138 — click on a single agent card. `position` is 0-based. */
export function trackClickAgent(agent: Agent, position: number): void {
    pulse.trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Click on agent in meglerpromo',
        object: {
            id: agent.phone,
            type: 'UIElement',
            elementType: 'Link',
            position: position.toString(),
            name: agent.orgId.toString(),
        },
        target: {
            id: `https://www.finn.no/meglerprofiler/${agent.phone}`,
            type: 'Page',
            name: agent.phone,
        },
        vertical: toVertical(),
    });
}

/** RE_ev139 — impression of the Pristips element. */
export function trackViewPristips(): void {
    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'View Pristips in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: 'https://www.finn.no/eiendom/pristips',
            type: 'Page',
        },
        vertical: toVertical(),
    });
}

/** RE_ev140 — click on the Pristips element. */
export function trackClickPristips(): void {
    pulse.trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Click on Pristips in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: 'https://www.finn.no/eiendom/pristips',
            type: 'Page',
        },
        vertical: toVertical(),
    });
}

/** RE_ev141 — impression of the sold map. */
export function trackViewSoldMap(): void {
    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'View sold map in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: 'https://www.finn.no/map/realestate/sold',
            type: 'Page',
        },
        vertical: toVertical(),
    });
}

/** RE_ev142 — click on the sold map. */
export function trackClickSoldMap(): void {
    pulse.trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Click on sold map in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Link',
        },
        target: {
            id: 'https://www.finn.no/map/realestate/sold',
            type: 'Page',
        },
        vertical: toVertical(),
    });
}

/** RE_ev143 — user scrolls the agent list via the carousel arrows. */
export function trackScrollList(): void {
    pulse.trackEvent({
        type: 'Click',
        intent: 'Update',
        name: 'Scroll in list in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Arrow',
        },
        vertical: toVertical(),
    });
}

/** RE_ev144 — impression of the Pristips form, shown in place of the carousel when no area is set. */
export function trackViewPristipsForm(): void {
    pulse.trackEvent({
        type: 'View',
        intent: 'Show',
        name: 'View Pristips form in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Form',
        },
        vertical: toVertical(),
    });
}

/** RE_ev145 — click on "Neste" in the Pristips form, taking the user from step 1 to step 2. */
export function trackClickPristipsFormNext(): void {
    pulse.trackEvent({
        type: 'Click',
        intent: 'Update',
        name: 'Click next in Pristips form in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Button',
            name: 'Neste',
        },
        vertical: toVertical(),
    });
}

/** RE_ev146 — click on "Se prisestimat", submitting the Pristips form. */
export function trackClickPristipsFormSubmit(): void {
    pulse.trackEvent({
        type: 'Click',
        intent: 'Open',
        name: 'Click estimate in Pristips form in meglerpromo',
        object: {
            id: MEGLERPROMO_ID,
            type: 'UIElement',
            elementType: 'Button',
            name: 'Se prisestimat',
        },
        target: {
            id: 'https://www.finn.no/eiendom/pristips/ditt-pristips',
            type: 'Page',
        },
        vertical: toVertical(),
    });
}
